// Import utils
import { getDocumentElement } from '../Utils/Utils.js';

/**
 * Create the fields that can be updated based off the value
 * of a selected primary field. The primary field always has
 * to be a select field. The other two fields need to be
 * standard input fields.
 * 
 * @param {object}      options 
 * 
 * @returns {object}
 */
const CreateCorrespondingValueFields = options => {
    const PrimaryFieldValues = options.data;
    const PrimaryField = getDocumentElement(options.selectors.primaryField);
    const SecondaryField = options.selectors.secondaryField.length > 0 ? getDocumentElement(options.selectors.secondaryField) : null;
    const TertiaryField = options.selectors.tertiaryField.length > 0 ? getDocumentElement(options.selectors.tertiaryField) : null;
    const IsClone = options.isClone;

    /**
     * Insert the field values for the primary field.
     * 
     * @returns {void}
     */
    const __populatePrimary = () => {
        if (IsClone) {
            PrimaryField.innerHTML = '';
        }

        PrimaryFieldValues.forEach((data, i) => {
            const Value = data.value !== null ? data.value : i;
            PrimaryField.add(new Option(data.name, Value));
        });
    };

    /**
     * Update the secondary field options in accordance to
     * the primary field selection.
     * 
     * @param   {object}    data
     * 
     * @returns {void}
     */
    const __updateFieldOptions = data => {
        const IntialOption = SecondaryField.options[0];
        
        // Clear the entire select field, so we avoid
        // having duplicate options.
        SecondaryField.innerHTML = '';

        // Re/populate the secondary field options in accordance
        // to the selected primary field value.
        SecondaryField.appendChild(IntialOption);
        data.secondaryField.forEach(item => {
            SecondaryField.add(new Option(item.name, item.value));
        });

        // Bind an event listener to the secondary field
        // if we have a tertiary field.
        if (TertiaryField && options.updateTertiaryOptions === true) {
            SecondaryField.addEventListener('input', e => {
                let tertiaryFieldData = data.secondaryField.find(item => item.value == SecondaryField.value);
                TertiaryField.value = tertiaryFieldData.tertiaryFieldValue;
            });
        }
    };

    /**
     * Bind event listener to the primary field.
     * 
     * @returns {void}
     */
    const __bind = () => {
        PrimaryField.addEventListener('input', () => {
            const Data = PrimaryFieldValues[PrimaryField.value];

            // If the secondary field options need to be updated
            // based on the selected primary field value, then
            // update the field options. Otherwise, set the secondary
            // and tertiary field values based on the selected primary
            // field value. This is for when the secondary field options
            // should remain static and don't need to be updated.
            if (options.updateSecondaryOptions) {
                __updateFieldOptions(Data);
            } else {
                // Set the corresponding field values based
                // on the selected primary field value.
                SecondaryField.value = Data.secondaryField;

                // If we have a tertiary field, set the value
                // based on the selected secondary field value.
                if (TertiaryField) {
                    TertiaryField.value = Data.tertiaryField;
                }
            }
        });
    };

    /**
     * Init the component — ensure that we have at least
     * a primary and secondary field present if we require dependent
     * field values. If we have only a primary field present, assume
     * that the primary field just requires us to populate it without
     * having any dependants.
     * 
     * @returns {void}
     */
    const init = () => {
        if (PrimaryField && SecondaryField) {
            __populatePrimary();
            __bind();
        } else if (PrimaryField && !SecondaryField) {
            __populatePrimary();
        } else {
            console.warn('Cannot find PrimaryField or SecondaryField', PrimaryField, SecondaryField);
        }
    };

    /**
     * Freeze the init in the return.
     */
    return Object.freeze({ init });
};

export default CreateCorrespondingValueFields;